import { Process } from 'shared/domain/process/types/model';
import {
  getAvailableFields,
  getStaticFieldsOnProcess,
} from '../visibleField/availableFields';
import { getIssueFormSchema } from './getIssueFormSchema';
import { IssueFieldNames } from './types/fieldNames';
import { IssueFormSchema } from './types/issueFormSchema';

const toggleableFieldNames = new Set(
  getAvailableFields()
    .filter((f) => f.canBeDisabled)
    .map((f) => f.fieldName)
);

const fieldDependencyMap = new Map<string, string[]>();
for (const field of getAvailableFields()) {
  if (field.dependsOn) {
    const deps = Array.isArray(field.dependsOn)
      ? field.dependsOn
      : [field.dependsOn];
    fieldDependencyMap.set(field.fieldName, deps);
  }
}

export interface IssueFormSchemaResult<T extends { fieldName: string; deleted?: boolean }> {
  schema: IssueFormSchema;
  visibleFieldMap: Map<string, T>;
}

export function getIssueFormSchemaWithRemovedUnknown<
  T extends { fieldName: string; deleted?: boolean },
>(
  visibleFields: T[],
  process: Process
): IssueFormSchemaResult<T> {
  const activeFields = visibleFields.filter((vf) => !vf.deleted);

  const visibleFieldMap = new Map<string, T>();
  for (const vf of activeFields) {
    visibleFieldMap.set(vf.fieldName, vf);
  }

  const schema = removeUnknown(
    getIssueFormSchema(),
    activeFields,
    process
  );

  return { schema, visibleFieldMap };
}

function removeUnknown(
  schema: IssueFormSchema,
  visibleFields: { fieldName: string }[],
  process: Process
): IssueFormSchema {
  const visibilityFields = visibleFields.filter((vf) =>
    toggleableFieldNames.has(vf.fieldName as IssueFieldNames)
  );
  const fields = [...visibilityFields, ...getStaticFieldsOnProcess(process)];
  const availableFieldNames = new Set(fields.map((f) => f.fieldName));

  const filteredSchema = schema.reduce<IssueFormSchema>(
    (result, section) => {
      const sectionFields = section.fields.filter((sectionFieldName) => {
        if (!availableFieldNames.has(sectionFieldName)) return false;
        const deps = fieldDependencyMap.get(sectionFieldName);
        if (deps) {
          return deps.every((dep) => availableFieldNames.has(dep));
        }
        return true;
      });
      if (sectionFields.length) {
        result.push({
          sectionTitleId: section.sectionTitleId,
          fields: sectionFields,
        });
      }
      return result;
    },
    []
  );

  return filteredSchema;
}
